<script>
import Title from "@/components/admin/common/TitleArea.vue"
import { saveAs } from 'file-saver'

const parseOrderIds = (value) => {
  return [...new Set(String(value || '')
    .split(/[\s,]+/)
    .map((token) => Number(token))
    .filter((id) => Number.isInteger(id) && id > 0))]
}

export default {
  name: 'AdminLogisSampleDataManage',
  components: {
    Title,
  },
  data() {
    return {
      description: {
        title: '운영 샘플 데이터'
      },
      exportForm: {
        dateFrom: '',
        dateTo: '',
        limit: 50,
        orderIdsText: '',
      },
      exportLoading: false,
      previewLoading: false,
      fulfillmentExportLoading: false,
      exportResult: null,
      previewResult: null,
      fulfillmentExportResult: null,
    }
  },
  computed: {
    envLabel() {
      const host = window.location.hostname
      if (host.includes('aonelogistics.com.au')) return '운영 서버'
      if (host.includes('localhost') || host.startsWith('127.') || host.startsWith('192.168.')) return '로컬 서버'
      return '개발 서버'
    },
    parsedOrderIds() {
      return parseOrderIds(this.exportForm.orderIdsText)
    }
  },
  methods: {
    formatDateOnly(date) {
      return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10)
    },
    buildExportPayload() {
      return {
        orderIds: this.parsedOrderIds,
        dateFrom: this.exportForm.dateFrom,
        dateTo: this.exportForm.dateTo,
        limit: this.exportForm.limit,
      }
    },
    validateExportScope() {
      if (this.parsedOrderIds.length === 0 && (!this.exportForm.dateFrom || !this.exportForm.dateTo)) {
        alert('기간 export를 위해 시작일과 종료일을 입력해야 합니다.')
        return false
      }
      return true
    },
    downloadBundle(bundle, fallbackName = 'logis-sample-bundle') {
      const fileName = `${bundle?.meta?.bundle_id || fallbackName}.json`
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      URL.revokeObjectURL(url)
    },
    downloadBlob(blob, contentDisposition, fallbackName) {
      const encodedName = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
      const quotedName = contentDisposition?.match(/filename="([^"]+)"/i)?.[1]
      const fileName = encodedName ? decodeURIComponent(encodedName) : (quotedName || fallbackName)
      saveAs(blob, fileName)
    },
    async previewBundle() {
      if (!this.validateExportScope()) return

      this.previewLoading = true
      this.previewResult = null
      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/logis-sample-data/preview',
          data: this.buildExportPayload(),
          name: '[ LOGIS / 샘플 데이터 preview ]'
        })
        this.previewResult = res?.data?.data || null
      } catch (error) {
        const message = error?.response?.data?.message || '샘플 데이터 미리보기에 실패했습니다.'
        alert(message)
      } finally {
        this.previewLoading = false
      }
    },
    async exportBundle() {
      if (!this.validateExportScope()) return

      this.exportLoading = true
      this.exportResult = null
      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/logis-sample-data/export',
          data: this.buildExportPayload(),
          name: '[ LOGIS / 샘플 데이터 export ]'
        })

        const bundle = res?.data?.data
        this.exportResult = bundle?.meta || null
        if (bundle) this.downloadBundle(bundle)
      } catch (error) {
        const message = error?.response?.data?.message || '샘플 데이터 내보내기에 실패했습니다.'
        alert(message)
      } finally {
        this.exportLoading = false
      }
    },
    async exportFulfillmentReference() {
      this.fulfillmentExportLoading = true
      this.fulfillmentExportResult = null
      try {
        const res = await this.$http({
          method: 'POST',
          url: '/api/logis-sample-data/fulfillment/export',
          responseType: 'blob',
          name: '[ LOGIS / Fulfillment 기준데이터 export ]'
        })
        const countsHeader = res?.headers?.['x-fulfillment-table-counts']
        this.fulfillmentExportResult = countsHeader ? JSON.parse(countsHeader) : null
        this.downloadBlob(
          res.data,
          res?.headers?.['content-disposition'],
          'fulfillment-reference.json.gz'
        )
      } catch (error) {
        let message = error?.response?.data?.message || 'Fulfillment 기준데이터 내보내기에 실패했습니다.'
        if (error?.response?.data instanceof Blob) {
          try {
            message = JSON.parse(await error.response.data.text())?.message || message
          } catch (_parseError) {
            // Keep the generic error when the binary response is not JSON.
          }
        }
        alert(message)
      } finally {
        this.fulfillmentExportLoading = false
      }
    }
  },
  mounted() {
    const today = new Date()
    const from = new Date(today)
    from.setDate(from.getDate() - 6)

    this.exportForm.dateFrom = this.formatDateOnly(from)
    this.exportForm.dateTo = this.formatDateOnly(today)
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description" />
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md">
      <div class="col-12">
        <div class="comp-box q-pa-md">
          <div class="text-subtitle1">현재 환경</div>
          <div class="text-body2 text-grey-7 q-mt-sm">
            {{ envLabel }}에서 사용하는 운영 샘플 데이터 도구입니다.
          </div>
          <div class="text-caption text-grey-6 q-mt-sm">
            운영에서는 원본 주문·상품·바코드와 연결된 샘플 데이터와 Fulfillment 기준데이터를 내보낼 수 있습니다.
          </div>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md">
      <div class="col-12">
        <div class="comp-box q-pa-md">
          <div class="text-subtitle1">1. 샘플 데이터 내보내기</div>
          <div class="text-caption text-grey-7 q-mt-sm">
            기본 방식은 기간 기준 export입니다. `ORDER_BASE.regDate` 기준으로 주문을 조회해 원본 JSON bundle을 다운로드합니다.
          </div>

          <div class="row q-col-gutter-md q-mt-md">
            <div class="col-12 col-md-4">
              <q-input
                v-model="exportForm.dateFrom"
                outlined
                label="시작일"
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div class="col-12 col-md-4">
              <q-input
                v-model="exportForm.dateTo"
                outlined
                label="종료일"
                placeholder="YYYY-MM-DD"
              />
            </div>
            <div class="col-12 col-md-4">
              <q-input
                v-model="exportForm.limit"
                outlined
                type="number"
                label="최대 건수"
              />
            </div>
          </div>

          <q-input
            v-model="exportForm.orderIdsText"
            type="textarea"
            autogrow
            outlined
            class="q-mt-md"
            label="주문 ID 목록 (선택)"
            hint="입력하면 기간 조건보다 우선합니다. 쉼표 또는 줄바꿈으로 여러 개 입력 가능"
          />

          <div class="text-caption text-grey-7 q-mt-sm">
            인식된 주문 수: {{ parsedOrderIds.length }}
          </div>

          <div class="row justify-end q-gutter-sm q-mt-md">
            <q-btn
              flat
              color="primary"
              label="미리보기"
              :loading="previewLoading"
              @click="previewBundle"
            />
            <q-btn
              color="primary"
              label="Bundle 생성"
              :loading="exportLoading"
              @click="exportBundle"
            />
          </div>

          <div v-if="exportResult" class="q-mt-md">
            <q-separator class="q-mb-md" />
            <div class="text-subtitle2">최근 export 결과</div>
            <div class="text-body2 q-mt-sm">Bundle ID: {{ exportResult.bundle_id }}</div>
            <div class="text-body2">Mode: {{ exportResult.export_mode }}</div>
            <div class="text-body2">주문 {{ exportResult.order_count }}건 / 콘솔 {{ exportResult.console_count }}건 / 아이템 {{ exportResult.item_count }}건</div>
          </div>

          <div v-if="previewResult" class="q-mt-md">
            <q-separator class="q-mb-md" />
            <div class="text-subtitle2">미리보기</div>
            <div class="text-body2 q-mt-sm">Mode: {{ previewResult.export_mode }}</div>
            <div class="text-body2">대상 주문 수: {{ previewResult.total }}</div>
            <q-list bordered separator class="q-mt-sm">
              <q-item v-for="row in previewResult.items" :key="row.id">
                <q-item-section>
                  <q-item-label>{{ row.orderId }} / {{ row.receiverName || '-' }}</q-item-label>
                  <q-item-label caption>
                    ID {{ row.id }} | regDate {{ row.regDate }} | partner {{ row.partnerId || '-' }} | status {{ row.status || '-' }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </div>
      </div>

    </div>

    <div class="row q-col-gutter-md q-mt-md">
      <div class="col-12">
        <div class="comp-box q-pa-md">
          <div class="text-subtitle1">2. Fulfillment 기준데이터 내보내기</div>
          <div class="text-caption text-grey-7 q-mt-sm">
            브랜드, 카테고리, 포장 단위, 마스터 SKU·규격, 파트너 SKU, 익스프레스 규칙을 압축 JSON으로 내려받습니다.
          </div>
          <q-banner dense rounded class="bg-blue-1 text-blue-10 q-mt-md">
            운영 DB에는 내보내기만 허용됩니다. 가져오기는 ledger 마이그레이션 적용 후 별도로 활성화합니다.
          </q-banner>

          <div class="row q-col-gutter-lg q-mt-sm">
            <div class="col-12">
              <div class="text-subtitle2">전체 기준데이터 내보내기</div>
              <div class="text-caption text-grey-7 q-mt-xs">
                현재 DB의 7개 기준 테이블 전체를 다운로드합니다.
              </div>
              <q-btn
                class="q-mt-md"
                color="primary"
                icon="download"
                label="Fulfillment JSON.GZ 다운로드"
                :loading="fulfillmentExportLoading"
                @click="exportFulfillmentReference"
              />
              <q-list v-if="fulfillmentExportResult" dense bordered separator class="q-mt-md">
                <q-item v-for="(count, table) in fulfillmentExportResult" :key="table">
                  <q-item-section>{{ table }}</q-item-section>
                  <q-item-section side>{{ count }}건</q-item-section>
                </q-item>
              </q-list>
            </div>

          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>
